import React, { useState, useMemo, useEffect, useCallback, startTransition } from 'react';
import { useNavigate } from 'react-router';
import { useGetClubsQuery } from '@slope/graphql/apollo';
import type { GetClubsQuery } from '@slope/graphql/apollo';
import { Flex } from '@slope/uikit';
import styled from 'styled-components';
import { SearchBar } from '../components/SearchBar';
import { HomeHeader } from '../components/HomeHeader';
import { LoadingState } from '../components/LoadingState';
import { ErrorState } from '../components/ErrorState';
import { EmptyState } from '../components/EmptyState';
import { ClubsGrid } from '../components/ClubsGrid';

type ClubFromQuery = NonNullable<GetClubsQuery['clubs']>['nodes'][0];

interface ClubWithDisabled extends ClubFromQuery {
  disabled?: boolean;
}

const StyledContainer = styled.div`
  min-height: 100vh;
  padding: 2rem;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  /* Allow scroll on this container */
  touch-action: pan-y;
`;

export const HomeView: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
  const [loadedClubIds, setLoadedClubIds] = useState<Set<string>>(new Set());

  // Handle search with view transition
  const handleSearchChange = useCallback(
    (value: string) => {
      if (hasInitiallyLoaded) {
        startTransition(() => {
          setSearchQuery(value);
        });
      } else {
        setSearchQuery(value);
      }
    },
    [hasInitiallyLoaded],
  );

  const { data, loading, error, fetchMore } = useGetClubsQuery({
    variables: {
      filters: [{ name: searchQuery || null }, { slug: searchQuery || null }],
      pagination: { first: 20 },
    },
    notifyOnNetworkStatusChange: true,
  });

  const [loadingMore, setLoadingMore] = useState(false);

  // Track if we've loaded data at least once
  useEffect(() => {
    if (data && !hasInitiallyLoaded) {
      setHasInitiallyLoaded(true);
    }
  }, [data, hasInitiallyLoaded]);

  // Simulate loading delay for new clubs
  useEffect(() => {
    const clubs = data?.clubs?.nodes || [];
    const newClubIds = clubs.map((club) => club.id);

    // Mark new clubs as loaded after a short delay (staggered)
    newClubIds.forEach((id, index) => {
      if (!loadedClubIds.has(id)) {
        setTimeout(() => {
          setLoadedClubIds((prev) => new Set(prev).add(id));
        }, index * 100); // Stagger by 100ms per card
      }
    });
  }, [data, loadedClubIds]);

  // Transform GraphQL data and add disabled state
  const filteredClubs: ClubWithDisabled[] = useMemo(() => {
    const graphqlClubs = data?.clubs?.nodes || [];
    const transformedClubs = graphqlClubs.map((club: ClubFromQuery) => ({
      ...club,
      // Only enable AM85, disable all others
      disabled: club.slug !== 'AM85',
    }));

    // Sort by enabled first (disabled: false comes before disabled: true)
    return transformedClubs.sort((a, b) => {
      if (a.disabled === b.disabled) return 0;
      return a.disabled ? 1 : -1;
    });
  }, [data]);

  const handleClubSelect = (clubSlug: string, disabled?: boolean) => {
    if (disabled) return;
    navigate(`/${clubSlug}`, { viewTransition: true });
  };

  // Infinite scroll handler
  const handleLoadMore = useCallback(async () => {
    const pageInfo = data?.clubs?.pageInfo;
    if (!pageInfo?.hasNextPage || loadingMore || loading) {
      return;
    }

    setLoadingMore(true);
    try {
      await fetchMore({
        variables: {
          filters: { name: searchQuery || null },
          pagination: {
            first: 20,
            after: pageInfo.endCursor,
          },
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;

          return {
            clubs: {
              ...fetchMoreResult.clubs,
              nodes: [...prev.clubs.nodes, ...fetchMoreResult.clubs.nodes],
            },
          };
        },
      });
    } catch (err) {
      console.error('Error loading more clubs:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [data, fetchMore, loading, loadingMore, searchQuery]);

  // Show full-page spinner only on initial load
  if (loading && !hasInitiallyLoaded) {
    return (
      <StyledContainer>
        <LoadingState />
      </StyledContainer>
    );
  }

  if (error) {
    return (
      <StyledContainer>
        <ErrorState />
      </StyledContainer>
    );
  }

  return (
    <StyledContainer>
      <Flex direction="column" alignItems="center">
        <HomeHeader />

        <SearchBar
          value={searchQuery}
          onChange={handleSearchChange}
          isLoading={loading && hasInitiallyLoaded && !loadingMore}
        />

        {!loading && filteredClubs.length === 0 ? (
          <EmptyState />
        ) : (
          <ClubsGrid
            clubs={filteredClubs}
            loadedClubIds={loadedClubIds}
            onClubSelect={handleClubSelect}
            hasMorePages={data?.clubs?.pageInfo?.hasNextPage}
            isLoadingMore={loadingMore}
            onLoadMore={handleLoadMore}
          />
        )}
      </Flex>
    </StyledContainer>
  );
};
